// HTE Stock reconciliation page — manager edits lot current_amount and byproduct buffer reserved_weight.
// Every mutation requires password + reason re-verification (handled server-side in inventory-service).
// Side-by-side responsive layout: lots on left, buffer on right.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Pencil, X, Check } from 'lucide-react';
import hteStockService, { HteStockLot, ByproductBuffer } from '../../services/hteStockService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { formatShortDate } from '../../utils/dateUtils';

// Pending action waiting for password + reason confirmation
interface PendingAction {
  type: 'updateLot' | 'updateBuffer' | 'deleteBuffer';
  id: string;
  value?: number;
  label: string;
}

const HteStockManagement: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Inline editing state
  const [editingLotId, setEditingLotId] = useState<string | null>(null);
  const [editingLotValue, setEditingLotValue] = useState('');
  const [editingBufferId, setEditingBufferId] = useState<string | null>(null);
  const [editingBufferValue, setEditingBufferValue] = useState('');

  // Password + reason modal state
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Queries
  const { data: lots = [], isLoading: lotsLoading } = useQuery(
    ['hte-stock-lots'],
    () => hteStockService.getLots()
  );

  const { data: buffers = [], isLoading: buffersLoading } = useQuery(
    ['hte-stock-buffers'],
    () => hteStockService.getBuffers()
  );

  // Mutations
  const updateLotMutation = useMutation(
    ({ id, current_amount, username, pw, rsn }: { id: string; current_amount: number; username: string; pw: string; rsn: string }) =>
      hteStockService.updateLot(id, current_amount, username, pw, rsn),
    {
      onSuccess: () => {
        toast.success(t('admin.hteStock.lotUpdated'));
        queryClient.invalidateQueries(['hte-stock-lots']);
        closeModal();
      },
      onError: (err: any) => {
        if (err?.response?.status === 401) setPasswordError(t('admin.hteStock.invalidCredentials'));
        else { toast.error(err?.response?.data?.error || t('admin.hteStock.updateFailed')); closeModal(); }
      },
    }
  );

  const updateBufferMutation = useMutation(
    ({ id, reserved_weight, username, pw, rsn }: { id: string; reserved_weight: number; username: string; pw: string; rsn: string }) =>
      hteStockService.updateBuffer(id, reserved_weight, username, pw, rsn),
    {
      onSuccess: () => {
        toast.success(t('admin.hteStock.bufferUpdated'));
        queryClient.invalidateQueries(['hte-stock-buffers']);
        closeModal();
      },
      onError: (err: any) => {
        if (err?.response?.status === 401) setPasswordError(t('admin.hteStock.invalidCredentials'));
        else { toast.error(err?.response?.data?.error || t('admin.hteStock.updateFailed')); closeModal(); }
      },
    }
  );

  const deleteBufferMutation = useMutation(
    ({ id, username, pw, rsn }: { id: string; username: string; pw: string; rsn: string }) =>
      hteStockService.deleteBuffer(id, username, pw, rsn),
    {
      onSuccess: () => {
        toast.success(t('admin.hteStock.bufferDeleted'));
        queryClient.invalidateQueries(['hte-stock-buffers']);
        closeModal();
      },
      onError: (err: any) => {
        if (err?.response?.status === 401) setPasswordError(t('admin.hteStock.invalidCredentials'));
        else { toast.error(err?.response?.data?.error || t('admin.hteStock.deleteFailed')); closeModal(); }
      },
    }
  );

  const isMutating = updateLotMutation.isLoading || updateBufferMutation.isLoading || deleteBufferMutation.isLoading;

  // Start editing a lot inline
  const startEditLot = (lot: HteStockLot) => {
    setEditingLotId(lot.id);
    setEditingLotValue(String(lot.current_amount));
    setEditingBufferId(null);
  };

  // Start editing a buffer inline
  const startEditBuffer = (buffer: ByproductBuffer) => {
    setEditingBufferId(buffer.id);
    setEditingBufferValue(String(buffer.reserved_weight));
    setEditingLotId(null);
  };

  // Submit inline edit → opens password modal
  const submitLotEdit = (lot: HteStockLot) => {
    const val = Number(editingLotValue);
    if (isNaN(val) || val < 0) { toast.error(t('admin.hteStock.invalidValue')); return; }
    setPendingAction({ type: 'updateLot', id: lot.id, value: val, label: `${lot.item_number}: ${lot.current_amount} → ${val}` });
    setEditingLotId(null);
  };

  const submitBufferEdit = (buffer: ByproductBuffer) => {
    const val = Number(editingBufferValue);
    if (isNaN(val) || val < 0) { toast.error(t('admin.hteStock.invalidValue')); return; }
    // Setting to 0 triggers delete
    if (val === 0) {
      setPendingAction({ type: 'deleteBuffer', id: buffer.id, label: `${buffer.byproduct_item_number}: ${buffer.reserved_weight} → 0 (${t('common.delete')})` });
    } else {
      setPendingAction({ type: 'updateBuffer', id: buffer.id, value: val, label: `${buffer.byproduct_item_number}: ${buffer.reserved_weight} → ${val}` });
    }
    setEditingBufferId(null);
  };

  // Execute the pending action after password + reason entry
  const executePendingAction = () => {
    if (!pendingAction || !password || !reason.trim()) return;
    const username = user?.username || user?.id || '';
    setPasswordError('');

    if (pendingAction.type === 'updateLot') {
      updateLotMutation.mutate({ id: pendingAction.id, current_amount: pendingAction.value!, username, pw: password, rsn: reason.trim() });
    } else if (pendingAction.type === 'updateBuffer') {
      updateBufferMutation.mutate({ id: pendingAction.id, reserved_weight: pendingAction.value!, username, pw: password, rsn: reason.trim() });
    } else if (pendingAction.type === 'deleteBuffer') {
      deleteBufferMutation.mutate({ id: pendingAction.id, username, pw: password, rsn: reason.trim() });
    }
  };

  const closeModal = () => {
    setPendingAction(null);
    setPassword('');
    setReason('');
    setPasswordError('');
  };

  return (
    <div className="space-y-6">
      {/* Side-by-side responsive layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Lots Section */}
        <div>
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">
            {t('admin.hteStock.lotsTitle')}
          </h3>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.itemNumber')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.itemName')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.currentAmount')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.receivedDate')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {lotsLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.loading')}</td></tr>
                ) : lots.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.noResults')}</td></tr>
                ) : (
                  lots.map((lot) => (
                    <tr key={lot.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">{lot.item_number}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 max-w-[180px] truncate">{lot.item_name || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {editingLotId === lot.id ? (
                          <input
                            type="number"
                            value={editingLotValue}
                            onChange={(e) => setEditingLotValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitLotEdit(lot); if (e.key === 'Escape') setEditingLotId(null); }}
                            className="w-24 px-2 py-1 text-sm text-right border border-primary-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500"
                            min="0"
                            step="any"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">{lot.current_amount}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">{formatShortDate(lot.received_date)}</td>
                      <td className="px-4 py-2.5 text-center">
                        {editingLotId === lot.id ? (
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => submitLotEdit(lot)} className="p-1 text-green-600 hover:text-green-700 rounded" title={t('common.save')}>
                              <Check className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditingLotId(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title={t('common.cancel')}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startEditLot(lot)} className="p-1 text-gray-400 hover:text-primary-600 rounded" title={t('common.edit')}>
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Byproduct Buffer Section */}
        <div>
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">
            {t('admin.hteStock.bufferTitle')}
          </h3>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.byproduct')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.itemName')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.hteStock.reservedWeight')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {buffersLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.loading')}</td></tr>
                ) : buffers.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.noResults')}</td></tr>
                ) : (
                  buffers.map((buffer) => (
                    <tr key={buffer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">{buffer.byproduct_item_number}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 max-w-[180px] truncate">{buffer.byproduct_name || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {editingBufferId === buffer.id ? (
                          <input
                            type="number"
                            value={editingBufferValue}
                            onChange={(e) => setEditingBufferValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitBufferEdit(buffer); if (e.key === 'Escape') setEditingBufferId(null); }}
                            className="w-24 px-2 py-1 text-sm text-right border border-primary-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500"
                            min="0"
                            step="any"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">{buffer.reserved_weight}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {editingBufferId === buffer.id ? (
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => submitBufferEdit(buffer)} className="p-1 text-green-600 hover:text-green-700 rounded" title={t('common.save')}>
                              <Check className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditingBufferId(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title={t('common.cancel')}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startEditBuffer(buffer)} className="p-1 text-gray-400 hover:text-primary-600 rounded" title={t('common.edit')}>
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Password + Reason Confirmation Modal */}
      {pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.hteStock.confirmPassword')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {pendingAction.type === 'deleteBuffer' ? t('admin.hteStock.deleteConfirmMsg') : t('admin.hteStock.updateConfirmMsg')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">{pendingAction.label}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.hteStock.reason')}
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('admin.hteStock.reasonPlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.hteStock.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && password && reason.trim()) executePendingAction(); }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {passwordError && (
                  <p className="text-sm text-red-500 mt-1">{passwordError}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeModal}
                disabled={isMutating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={executePendingAction}
                disabled={!password || !reason.trim() || isMutating}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  pendingAction.type === 'deleteBuffer'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isMutating ? t('common.processing') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HteStockManagement;
